(() => {
  const initializedGalleries = new WeakSet();

  class VariantMediaGrouping {
    constructor(gallery) {
      this.gallery = gallery;
      this.productInfo = gallery.closest('product-info');
      this.sectionId = gallery.dataset.sectionId;
      this.productHandle = gallery.dataset.productHandle;
      this.optionNames = (gallery.dataset.mediaGroupingOptions || 'Color')
        .split(',')
        .map((name) => name.trim().toLowerCase())
        .filter(Boolean);
      this.mediaGroups = new Map();
      this.variantGroups = new Map();
      this.handleVariantChange = this.handleVariantChange.bind(this);
    }

    async init() {
      if (!this.productInfo || !this.sectionId || !this.productHandle || this.optionNames.length === 0) return;

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}products/${encodeURIComponent(this.productHandle)}.js`
        );

        if (!response.ok) throw new Error(`Product request failed with status ${response.status}`);

        const product = await response.json();
        const groupingOption = product.options.find((option) =>
          this.optionNames.includes(option.name.trim().toLowerCase())
        );

        if (!groupingOption) return;

        this.buildGroups(product, groupingOption);
        this.productInfo.addEventListener('change', this.handleVariantChange);
        this.showSelectedVariantGroup();
      } catch (error) {
        console.error('Unable to initialize variant media grouping:', error);
      }
    }

    buildGroups(product, groupingOption) {
      const optionIndex = groupingOption.position - 1;
      const groupStarts = [];

      groupingOption.values.forEach((value) => {
        const variantWithMedia = product.variants.find(
          (variant) => variant.options[optionIndex] === value && variant.featured_media
        );

        if (variantWithMedia) {
          groupStarts.push({
            name: value,
            position: variantWithMedia.featured_media.position,
          });
        }
      });

      groupStarts.sort((a, b) => a.position - b.position);

      groupStarts.forEach((group, index) => {
        const nextGroup = groupStarts[index + 1];

        product.media.forEach((media, mediaIndex) => {
          const position = mediaIndex + 1;

          if (position >= group.position && (!nextGroup || position < nextGroup.position)) {
            this.mediaGroups.set(String(media.id), group.name);
          }
        });
      });

      product.variants.forEach((variant) => {
        const groupName = variant.options[optionIndex];

        if (groupStarts.some((group) => group.name === groupName)) {
          this.variantGroups.set(String(variant.id), groupName);
        }
      });
    }

    handleVariantChange(event) {
      if (!event.target.matches('input.product-variant-id[name="id"]')) return;

      this.showGroupForVariant(event.target.value);
    }

    showSelectedVariantGroup() {
      const selectedVariantInput = this.productInfo.querySelector('input.product-variant-id[name="id"]');

      this.showGroupForVariant(selectedVariantInput?.value);
    }

    showGroupForVariant(variantId) {
      const selectedGroup = this.variantGroups.get(String(variantId));

      this.gallery.querySelectorAll('li[data-media-id], li[data-target]').forEach((mediaItem) => {
        const mediaReference = mediaItem.dataset.mediaId || mediaItem.dataset.target;
        const mediaId = mediaReference?.startsWith(`${this.sectionId}-`)
          ? mediaReference.slice(this.sectionId.length + 1)
          : null;
        const mediaGroup = mediaId ? this.mediaGroups.get(mediaId) : null;

        mediaItem.hidden = Boolean(selectedGroup && mediaGroup && mediaGroup !== selectedGroup);
      });

      window.requestAnimationFrame(() => {
        this.gallery.querySelectorAll('slider-component').forEach((slider) => {
          if (typeof slider.resetPages === 'function') {
            slider.resetPages();
          } else if (typeof slider.initPages === 'function') {
            slider.initPages();
          }
        });
      });
    }
  }

  const initializeVariantMediaGrouping = (container = document) => {
    container.querySelectorAll('media-gallery[data-variant-media-grouping]').forEach((gallery) => {
      if (initializedGalleries.has(gallery)) return;

      initializedGalleries.add(gallery);
      new VariantMediaGrouping(gallery).init();
    });
  };

  initializeVariantMediaGrouping();

  document.addEventListener('shopify:section:load', (event) => {
    initializeVariantMediaGrouping(event.target);
  });
})();
